import styled, { css, keyframes } from 'styled-components';
import { H6 } from '@components/dashboard/Typography';
import Colors from '@components/shared/Colors';
import { MEDIA, NAV_MENU_BREAKPOINT, PPC_INTRO_BACKGROUND, PPC_INTRO_BACKGROUND_MOBILE, PPC_INTRO_BACKGROUND_TABLET } from '@constants';
import { StyledButtonProps } from '@contracts';
import LinkTranslated from './LinkTranslated';

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export const InputLabel = styled(H6)`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;
  margin-bottom: 0;
  letter-spacing: 0;
  text-align: left;
  color: inherit;
`;

interface InputStyledProps extends React.InputHTMLAttributes<HTMLInputElement> {
  'data-recurly'?: string;
  'data-cy'?: string;
  $error?: any;
  $mobile?: boolean;
  $marginBottom?: string;
  $height?: string;
}

export const InputStyled = styled.input<InputStyledProps>`
  background: ${Colors.cream};
  box-sizing: border-box;
  border-radius: 2px;
  height: ${(props) => (props.$height ? props.$height : '54px')};
  padding: 0 20px;
  margin-bottom: ${(props) => (props.$marginBottom ? props.$marginBottom : '20px')};

  width: 100%;
  border: 1px solid ${Colors.paleGold};
  border-color: ${(props) => (props.$error ? Colors.red : Colors.paleGold)};

  font-family: 'BrownStd';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0;
  text-align: left;
  color: ${Colors.navyBlue};

  ${(props) => {
    if (props.$mobile) {
      return ``;
    }

    return '';
  }}

  &::placeholder {
    padding-top: 2px;
    color: ${Colors.navyBlue};
  }
`;

export const PhoneInputContainer = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
  align-items: center;

  .react-tel-input {
    display: flex;
  }

  div {
    .flag-dropdown.open {
      background: ${Colors.cream};
      .selected-flag {
        background: ${Colors.cream};
      }
    }

    .phone-input {
      border: 1px solid ${Colors.paleGold}!important;
      background: ${Colors.cream}!important;
      color: ${Colors.navyBlue};
      font-family: 'BrownStd';
      border-radius: 2px;
      margin-left: 100px;
      padding: 15px;
      background: transparent;
      height: auto;
      flex-grow: 1;

      &.invalid-number {
        border-color: ${Colors.red}!important;
      }
    }

    .phone-button {
      border: 1px solid ${Colors.paleGold}!important;
      border-radius: 2px;
      margin-right: 50px;
      background: transparent;
      padding-inline: 20px;

      &.invalid-number {
        border-color: ${Colors.red};
      }

      .selected-flag {
        background: ${Colors.cream};
        padding: 0;
        .arrow {
          margin-left: 10px;
        }
      }
    }

    .phone-dropdown {
      box-shadow: none;
      border: 1px solid ${Colors.gold};

      .search {
        background: ${Colors.cream};
        margin: 0;
        padding: 0;
        border-bottom: 1px solid ${Colors.ecru};

        .search-box {
          width: 100%;
          height: auto;
          font-family: 'BrownStd';
          background: #0000;
          border: 0;
          text-transform: capitalize;
          display: block;
          padding: 10px 15px;
          font-size: 14px;
        }

        .search-emoji {
          display: none;
        }
      }

      .country {
        padding: 6px 15px;
        font-size: 12px;

        :hover {
          background: ${Colors.ecru};
        }
      }

      position: absolute;
      left: 0;

      max-height: 300px;
      background: ${Colors.cream};
      font-family: 'BrownStd';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 150%;

      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
  }
`;

export const StyledLinkWrapper = styled(LinkTranslated)`
  display: inline-flex;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;

  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    outline: none;
  }
`;

export const ButtonStyleTemplate = css<StyledButtonProps>`
  background: ${(props) => (props.$backgroundColor ? props.$backgroundColor : Colors.lilac)};
  color: ${(props) => (props.$buttonTextColor ? props.$buttonTextColor : Colors.cream)};
  border: 0;
  border-radius: ${(props) => props.$borderRadius || '5px'};
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  letter-spacing: 1px;
  text-align: center;
  cursor: pointer;
  display: ${(props) => (props.$isInline ? 'inline-flex' : 'flex')};
  width: ${(props) => (props.$isInline ? 'fit-content' : 'auto')};
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
  text-decoration: none;

  ${({ $size }) => {
    const defaultStyles = `
      height: 50px;
      padding: 0 26px;
      font-size: 14px;
      text-transform: uppercase;
    `;

    switch ($size) {
      case 'small':
        return `
          height: 44px;
          padding: 0 25px;
          font-size: 12px;
          text-transform: uppercase;
        `;
      case 'small-round':
        return `
          padding: 12px;
          height: 32px;
          border-radius: 24px;
          font-size: 14px;
          text-transform: none;
        `;
      case 'large':
      default:
        return defaultStyles;
    }
  }}

  &:hover {
    background: ${Colors.forestGreen};
    color: ${Colors.cream};
    text-decoration: none;
  }

  @media (max-width: 768px) {
    ${({ $size }) => {
      if ($size === 'small-round') return '';
      return 'padding: 0 18px;';
    }}
  }
`;

export const NavPreviewItemsWrapper = styled.div`
  display: flex;
  gap: 40px;
  padding-top: 0;
  padding-bottom: 0;
  max-width: 580px;
  margin: 0;

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    gap: 25px;
    padding-top: 60px;
    padding-bottom: 40px;
    max-width: 325px;
    margin: 0 auto;
  }
`;

export const NavPreviewItem = styled(LinkTranslated)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  img {
    border-radius: 12px;
    width: 270px;
    max-width: 270px;
    height: 270px;
  }

  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    img {
      border-radius: 3.409px;
      width: 150px;
      max-width: 150px;
      height: 150px;
    }
  }
`;

interface IPrice {
  $color: string;
}

export const StyledProductPrice = styled.div<IPrice>`
  font-family: 'Domaine';
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  letter-spacing: 0;
  padding: 0 18px;
  height: 50px;
  line-height: 50px;
  text-align: center;
  color: ${Colors.navyBlue};
  background: ${(props) => (props.$color !== '' ? props.$color : `${Colors.paleGold50}`)};
  border-radius: 2px 0 0 2px;
  min-width: 100px;
`;

export const popUpAnimation = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.01) rotate(0.5deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
`;

export const StyledSimpleBoxWrapper = styled.div<{
  $selectable?: boolean;
  $isSelected?: boolean;
  $isHighlighted?: boolean;
  $isRadioSelection?: boolean;
}>`
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  border: ${({ $isSelected, $isHighlighted }) =>
    $isSelected ? `1px solid ${Colors.gold}` : `1px solid ${$isHighlighted ? Colors.sky : Colors.paleGold}`};
  cursor: ${({ $selectable, $isSelected, $isRadioSelection }) =>
    $selectable && (!$isRadioSelection || ($isRadioSelection && !$isSelected)) ? 'pointer' : 'default'};
  background-color: ${({ $isSelected, $isHighlighted }) => ($isSelected ? Colors.ecru50 : `${$isHighlighted ? Colors.babyBlue25 : Colors.cream}`)};
  border-radius: 12px;
  box-shadow: ${({ $isSelected }) => ($isSelected ? `inset 0 0 0 1px ${Colors.gold}` : 'none')};
  user-select: none;
  -webkit-user-select: none;

  ${({ $isSelected, $selectable }) =>
    $isSelected && $selectable
      ? css`
          animation: ${popUpAnimation} 0.2s ease-in-out;
        `
      : ''}
`;

export const SubscriptionModalTriggerButton = styled.button`
  background: #fcf3df;
  border: 1px solid ${Colors.ecru};
  box-sizing: border-box;
  padding: 11px 15px;
  transition: all 0.15s ease;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: ${Colors.ecru};
  }

  div {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${Colors.gold};
    color: ${Colors.cream};
    display: inline-block;
    font-family: BrownStd;
    font-weight: bold;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    margin-right: 15px;
  }

  span {
    font-family: BrownStd;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-align: center;
    text-transform: uppercase;
    color: ${Colors.darkGreen};

    @media (max-width: 768px) {
      letter-spacing: 1px;
    }
  }
`;

export const Gradient2Style = css`
  background: linear-gradient(92deg, #cf6aa7 48.19%, #ea534f 90.76%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  padding: 0 0.15em 0 0; // fixes left clipping when both gradient and italic
  margin-right: -0.15em;
`;

export const Note = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  background: ${Colors.mint50};
  border-radius: 5px;
  border: 1px solid ${Colors.mint};
  width: 100%;

  & > svg {
    flex-shrink: 0;
  }
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 29, 59, 0.3);
  border-radius: 8px;
  pointer-events: none;
  width: 100%;
  height: 100%;
`;

export const DashboardTopBackground = styled.div<{ $background?: string }>`
  position: absolute;
  top: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  background: ${(props) => props.$background || 'linear-gradient(180deg, #EAE9F9 0%, rgba(234, 233, 249, 0.50) 100%)'};
  border-radius: 0 0 150px 150px;

  ${MEDIA.laptop} {
    border-radius: 0 0 140px 140px;
  }

  ${MEDIA.tablet} {
    border-radius: 0 0 130px 130px;
  }

  ${MEDIA.mobileL} {
    border-radius: 0 0 120px 120px;
  }

  ${MEDIA.mobileM} {
    border-radius: 0 0 110px 110px;
  }

  ${MEDIA.mobileS} {
    border-radius: 0 0 100px 100px;
  }
`;

export const PPCIntroTopBackground = styled.img`
  position: absolute;
  top: 0;
  z-index: 1;
  width: 100%;
  min-height: 500px;
  content: url(${PPC_INTRO_BACKGROUND});
  border-radius: 0 0 200px 200px;
  object-fit: cover;

  ${MEDIA.laptop} {
    content: url(${PPC_INTRO_BACKGROUND_TABLET});
    max-height: 450px;
    min-height: auto;
  }

  ${MEDIA.mobileL} {
    content: url(${PPC_INTRO_BACKGROUND_MOBILE});
    min-height: 400px;
  }
`;

export const PPCDashboardTopBackground = styled(PPCIntroTopBackground)`
  z-index: -1;
  height: 100%;
  left: -64px;
  width: calc(100% + 128px);
  max-width: calc(100% + 128px);
  border-radius: 0 0 300px 300px;

  ${MEDIA.laptop} {
    max-height: 100%;
  }

  ${MEDIA.mobileL} {
    left: -24px;
    width: calc(100% + 48px);
    max-width: calc(100% + 48px);
  }
`;

export const DashboardReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  isolation: isolate;
  width: 900px;
  max-width: 100%;
  background: ${Colors.cream};
  box-shadow: 0 2px 2px rgba(0, 29, 59, 0.1);
  border-radius: 3px;
  color: ${Colors.navyBlue};

  margin-bottom: 100px;

  ${MEDIA.laptopL} {
    width: 800px;
  }

  ${MEDIA.laptopM} {
    width: 760px;
    margin-bottom: 40px;
  }
`;

export const VideoThumbnail = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  box-shadow: 0px 13px 22px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  object-fit: cover;
`;

export const VerticalLinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${MEDIA.tablet} {
    gap: 4px;
  }
`;

export const SectionArea = styled.div`
  padding: 16px;
  background-color: ${Colors.paleGold25};
  border-radius: 8px;
`;

export const LeftOffsetPercentageContainer = styled.div<{
  $valuePercentWidth: number;
  $currentWidth: number | undefined;
  $maxWidth: number;
  $offset?: number;
}>`
  position: relative;
  left: ${({ $valuePercentWidth, $currentWidth, $maxWidth, $offset }) => {
    const currentWidth = $currentWidth || 0;
    const offset = $offset || 0;
    const halfCurrentWidth = currentWidth / 2;
    const left = $valuePercentWidth - halfCurrentWidth;

    if (left < offset) {
      return `${offset}px`;
    }

    if (left > $maxWidth - currentWidth - offset) {
      return `${$maxWidth - currentWidth - offset}px`;
    }

    return `${left}px`;
  }};
`;

export const FullWidthContainer = styled.div`
  width: 100%;
  padding: 0 60px;

  ${MEDIA.tablet} {
    padding: 0 24px;
  }
`;

export const OuterWidthContainer = styled.div`
  margin: 0 -60px;

  > div {
    padding-left: 60px;
    padding-right: 60px;
  }

  ${MEDIA.tablet} {
    margin: 0 -24px;

    > div {
      padding-left: 24px;
      padding-right: 24px;
    }
  }
`;
